import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  describeInvitation,
  acceptInvitation,
  getStoredToken,
  getErrorMessage,
} from '@/lib/api';
import { Icon, Loading } from '@/components/ui';

const ROLE_LABEL: Record<string, string> = {
  owner: 'Propriétaire', editor: 'Éditeur', analyst: 'Analyste', viewer: 'Lecteur',
};

/**
 * Page publique d'acceptation d'invitation (B7d) — route /invite/:token.
 * Montre le workspace + rôle. Si le compte n'existe pas encore, l'invité·e
 * définit SON mot de passe (jamais imposé). Après acceptation → connexion.
 */
export default function InvitePage(): JSX.Element {
  const { token = '' } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const q = useQuery({
    queryKey: ['invitation', token],
    queryFn: () => describeInvitation(token),
    enabled: Boolean(token),
    retry: false,
  });

  const handleAccept = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await acceptInvitation(token, password || undefined);
      setDone(true);
    } catch (err) {
      setError(getErrorMessage(err, "L'acceptation a échoué."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen bg-bg flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md space-y-5">
        <div className="brand-mark justify-center"><span className="brand-dot">W</span> WebForge Survey</div>

        {q.isPending && <div className="card"><Loading label="Vérification de l'invitation…" /></div>}

        {q.isError && (
          <div className="card text-center space-y-3">
            <Icon.AlertTriangle size={28} className="mx-auto text-danger" />
            <h1 className="text-lg">Invitation invalide ou expirée</h1>
            <p className="text-sm text-text-muted">
              Ce lien n'est plus valable. Demandez une nouvelle invitation à votre référent.
            </p>
            <Link to="/login" className="btn-secondary inline-flex">Aller à la connexion</Link>
          </div>
        )}

        {q.data && done && (
          <div className="card text-center space-y-3">
            <Icon.Check size={28} className="mx-auto text-success" />
            <h1 className="text-lg">Invitation acceptée</h1>
            <p className="text-sm text-text-muted">
              Vous avez rejoint <strong>{q.data.workspaceName}</strong> en tant que {ROLE_LABEL[q.data.role] ?? q.data.role}.
            </p>
            <button type="button" className="btn-primary w-full" onClick={() => navigate('/login')}>
              Se connecter
            </button>
          </div>
        )}

        {q.data && !done && q.data.status !== 'pending' && (
          <div className="card text-center space-y-3">
            <Icon.Info size={28} className="mx-auto text-text-muted" />
            <h1 className="text-lg">{q.data.status === 'accepted' ? 'Invitation déjà acceptée' : 'Invitation expirée'}</h1>
            <Link to="/login" className="btn-secondary inline-flex">Aller à la connexion</Link>
          </div>
        )}

        {q.data && !done && q.data.status === 'pending' && (
          <div className="card space-y-4">
            <header className="space-y-1">
              <h1 className="text-lg">Rejoindre « {q.data.workspaceName} »</h1>
              <p className="text-sm text-text-muted">
                Vous êtes invité·e en tant que <strong>{ROLE_LABEL[q.data.role] ?? q.data.role}</strong>{' '}
                ({q.data.email}).
              </p>
            </header>

            {!getStoredToken() && (
              <div className="field">
                <label htmlFor="inv-pw" className="field-label">
                  Mot de passe <span className="text-text-muted font-normal">(si vous n'avez pas encore de compte)</span>
                </label>
                <input id="inv-pw" type="password" autoComplete="new-password" className="input-base"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="Choisissez un mot de passe" />
                <p className="field-hint">
                  Si vous avez déjà un compte avec cet e-mail, laissez vide — l'invitation y sera rattachée.
                </p>
              </div>
            )}

            {error && (
              <p role="alert" className="field-error"><Icon.AlertTriangle /><span>{error}</span></p>
            )}

            <button type="button" className="btn-primary w-full btn-lg" disabled={busy} onClick={handleAccept}>
              {busy ? 'Acceptation…' : 'Accepter l\'invitation'}
            </button>
            <p className="text-xs text-text-muted text-center">
              En acceptant, vous rejoignez ce workspace. Vous pourrez ensuite vous connecter.
            </p>
          </div>
        )}
      </div>
    </main>
  );
}
